import { ReactElement, useState } from 'react';
import { GetOverviewSummary } from '../types';
import { useTranslation } from 'react-i18next';
import { UnitsTooltipContent, UnitTooltipContainer } from './styles';
import { BigNumberCard } from './components/Card';
import { useHistory } from 'react-router-dom';
import { handleFilterStateRoute } from '../helpers/filters';
import { useFilterContext } from '../contexts/FilterContext';
import { getUserProfile } from 'helpers/userProfile';
import { normalizeNumber } from '../helpers/numbers';

type UnitsData = {
  units: GetOverviewSummary['UNITS'];
  unitsBySolution: GetOverviewSummary['UNITS_BY_SOLUTION'];
} | undefined;

interface UnitsProps {
  data: UnitsData;
  loading: boolean;
}

type UnitsTooltipProps = Pick<UnitsProps, 'data'>;

interface TooltipRowProps {
  label: string;
  value?: number;
  subtext?: string;
}

export function Units({
  data,
  loading,
}: Readonly<UnitsProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const { t } = useTranslation();
  const { selectedOptions, clients } = useFilterContext();

  function onClickUnitsDetails(): void {
    history.push('/analise/unidades',
      handleFilterStateRoute(clients, selectedOptions, profile));
  }

  return (
    <BigNumberCard
      id="unit-count"
      icon="Building3Icon"
      number={String(normalizeNumber(data?.units.TOTAL))}
      description={{
        text: t('unidadesSobGestao'),
        tooltip: <UnitsTooltip data={data} />,
      }}
      subDescription={{
        number: normalizeNumber(data?.units.OPERATIONAL),
        text: t('unidadesOperacao'),
      }}
      onClickDetails={onClickUnitsDetails}
      loading={loading}
      disabledTooltip={loading || !data}
      dataTestId="big-number-card-unit"
    />
  );
}

function UnitsTooltip({
  data,
}: Readonly<UnitsTooltipProps>): ReactElement {
  const { t } = useTranslation();

  return (
    <UnitTooltipContainer>
      <div>
        <UnitsTooltipContent>
          <h3>{t('unidades')}</h3>
          <TooltipRow label={t('monitoradas')} value={normalizeNumber(data?.units.MONITORED)} />
          <TooltipRow label={t('emOperacao')} value={normalizeNumber(data?.units.OPERATIONAL)} subtext={t('unidadesOperandoCorretamente')} />
          <TooltipRow label={t('foraDeOperacao')} value={normalizeNumber(data?.units.INACTIVE)} subtext={t('unidadesEmReformaOuEmDesinstalaçãoForcadaDosIoT')} />
          {data?.units.INSTALLATION !== undefined && (
          <TooltipRow label={t('emInstalacao')} value={normalizeNumber(data.units.INSTALLATION)} />
          )}
        </UnitsTooltipContent>
      </div>
      <UnitsTooltipContent style={{ width: 300 }}>
        <h3>{t('porSolucao')}</h3>
        <TooltipRow label={t('agua')} value={normalizeNumber(data?.unitsBySolution.WATER)} />
        <TooltipRow label={t('automacao')} value={normalizeNumber(data?.unitsBySolution.AUTOMATION)} />
        <TooltipRow label={t('energia')} value={normalizeNumber(data?.unitsBySolution.ENERGY)} />
        <TooltipRow label={t('saude')} value={normalizeNumber(data?.unitsBySolution.HEALTH)} />
      </UnitsTooltipContent>
    </UnitTooltipContainer>
  );
}

function TooltipRow({
  label,
  value,
  subtext,
}: Readonly<TooltipRowProps>): ReactElement {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1, lineHeight: 1.2,
    }}
    >
      <span>
        {value === undefined ? label : `${label}: ${normalizeNumber(value)}`}
      </span>
      {subtext && (
        <span style={{ lineHeight: 1.2 }}>
          {' '}
          {subtext}
        </span>
      )}
    </div>
  );
}
